import { useState, useEffect, useCallback } from 'react';
import {
  Key,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Volume2,
  Save,
  ChevronDown,
  ChevronUp,
  BookOpen,
  BarChart3,
  Clock,
  Phone,
  MessageSquare,
  Shield,
  Webhook,
  Copy,
  Link,
} from 'lucide-react';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { getErrorMessage } from '../../lib/utils';


interface ElevenLabsConfig {
  id: string;
  tenant_id: string;
  api_key_hint: string;
  is_active: boolean;
  is_valid: boolean;
  last_tested_at: string | null;
  default_voice_id: string;
  default_llm_provider: string;
  default_llm_model: string;
  webhook_secret_configured: boolean;
  webhook_secret_hint: string | null;
}

// Analytics ElevenLabs: valori numerici possono arrivare come stringa
// (pg numeric sul wire), per questo number | string sui campi aggregati.
interface ElevenLabsAnalyticsConversation {
  id?: string;
  status?: string;
  agent_name?: string;
  created_at?: string;
  duration_seconds?: number | string;
}

interface ElevenLabsAnalytics {
  stats?: {
    total_conversations?: number | string;
    avg_duration?: number | string;
    completed?: number | string;
    total_duration?: number | string;
  };
  recent?: ElevenLabsAnalyticsConversation[];
}

export default function ElevenLabsConfigTab() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [savingApiKey, setSavingApiKey] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState<ElevenLabsConfig | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [showDocs, setShowDocs] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analytics, setAnalytics] = useState<ElevenLabsAnalytics | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<{ data?: ElevenLabsConfig } | ElevenLabsConfig>('/elevenlabs/config');
      const rawData = res?.data as { data?: ElevenLabsConfig } | ElevenLabsConfig | undefined;
      const configData = ((rawData as { data?: ElevenLabsConfig } | undefined)?.data ?? rawData ?? null) as ElevenLabsConfig | null;
      setConfig(configData);
      if (configData) {
        setIsActive(configData.is_active);
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await apiClient.get<{ data?: ElevenLabsAnalytics } | ElevenLabsAnalytics>('/elevenlabs/analytics');
      const rawData = res?.data as { data?: ElevenLabsAnalytics } | ElevenLabsAnalytics | undefined;
      const analyticsData = ((rawData as { data?: ElevenLabsAnalytics } | undefined)?.data ?? rawData ?? null) as ElevenLabsAnalytics | null;
      setAnalytics(analyticsData);
    } catch (error) {
      toast({ title: 'Errore', description: 'Impossibile caricare analytics', variant: 'destructive' });
    } finally {
      setAnalyticsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (showAnalytics && !analytics) {
      fetchAnalytics();
    }
  }, [showAnalytics, analytics, fetchAnalytics]);

  const handleSaveApiKey = async () => {
    setSavingApiKey(true);
    try {
      const payload: { is_active: boolean; api_key?: string } = { is_active: isActive };
      if (apiKey) {
        payload.api_key = apiKey;
      }
      await apiClient.post('/elevenlabs/config', payload);
      toast({ title: 'Salvato', description: 'API Key e stato servizio aggiornati' });
      setApiKey('');
      await fetchData();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSavingApiKey(false);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    setWebhookTestResult(null);
    try {
      const res = await apiClient.post<{ success: boolean; error?: string; data?: { success?: boolean } }>('/elevenlabs/config/test-webhook');
      const result = res?.data || res;
      setWebhookTestResult(result);
      if (result.success || result?.data?.success) {
        toast({ title: 'Webhook funzionante', description: 'Il webhook risponde correttamente' });
      } else {
        toast({ title: 'Webhook non raggiungibile', description: result.error || 'Verifica la configurazione', variant: 'destructive' });
      }
    } catch (error) {
      setWebhookTestResult({ success: false, error: getErrorMessage(error) });
      toast({ title: 'Errore test webhook', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleSaveWebhook = async () => {
    if (!webhookSecret.trim()) {
      toast({ title: 'Errore', description: 'Inserisci il webhook secret', variant: 'destructive' });
      return;
    }
    setSavingWebhook(true);
    try {
      await apiClient.post('/elevenlabs/config', { webhook_secret: webhookSecret });
      toast({ title: 'Salvato', description: 'Webhook secret aggiornato' });
      setWebhookSecret('');
      await fetchData();
      handleTestWebhook();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const keyToTest = apiKey || '';
      if (!keyToTest && !config?.api_key_hint) {
        toast({ title: 'Errore', description: 'Inserisci una API Key per testare la connessione', variant: 'destructive' });
        setTesting(false);
        return;
      }
      const res = await apiClient.post<{ success: boolean; error?: string; data?: { success?: boolean } }>('/elevenlabs/config/test', {
        api_key: keyToTest || undefined,
      });
      const result = res?.data || res;
      setTestResult(result);
      if (result.success || result?.data?.success) {
        toast({ title: 'API Key valida', description: 'Connessione ElevenLabs verificata' });
        await fetchData();
      } else {
        toast({ title: 'API Key non valida', description: result.error || 'Verifica la chiave inserita', variant: 'destructive' });
      }
    } catch (error) {
      setTestResult({ success: false, error: getErrorMessage(error) });
      toast({ title: 'Errore test API Key', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SEZIONE 1: API Key e Connessione */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Key className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Chiave API ElevenLabs</h3>
            <p className="text-sm text-muted-foreground">Connessione alla piattaforma ElevenLabs</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <Label className="text-sm font-medium text-foreground mb-1.5 block">API Key</Label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={config?.api_key_hint ? `Chiave attuale: ${config.api_key_hint}` : 'Inserisci xi-api-key da elevenlabs.io'}
                className="pl-10 bg-muted border-border text-foreground"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              La trovi su elevenlabs.io → Profilo → API Keys (inizia con "xi_")
            </p>
          </div>

          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-border">
            {config?.is_valid ? (
              <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                {config?.is_valid ? 'API Key verificata' : 'API Key non verificata'}
              </p>
              {config?.last_tested_at && (
                <p className="text-xs text-muted-foreground">
                  Ultimo test: {new Date(config.last_tested_at).toLocaleString('it-IT')}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <Label htmlFor="elevenlabs-active" className="text-sm text-foreground">Abilita ElevenLabs per questo tenant</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="elevenlabs-active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              {isActive ? (
                <Badge className="bg-emerald-100 text-emerald-700">Attivo</Badge>
              ) : (
                <Badge className="bg-muted text-muted-foreground">Disattivato</Badge>
              )}
            </div>
          </div>

          {testResult && (
            <div className={`p-3 rounded-lg border ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )}
                <span className={`text-sm font-medium ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {testResult.success ? 'API Key valida - connessione riuscita' : 'API Key non valida - controlla la chiave'}
                </span>
              </div>
              {!testResult.success && testResult.error && (
                <p className="text-xs text-red-600 mt-1">{testResult.error}</p>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              onClick={handleTest}
              disabled={testing || (!apiKey && !config?.api_key_hint)}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Testa API Key
            </Button>
            <Button
              onClick={handleSaveApiKey}
              disabled={savingApiKey}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {savingApiKey ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salva API Key
            </Button>
          </div>
        </div>
      </div>

      {/* SEZIONE 2: Webhook Secret (separata) */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Shield className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Webhook Secret</h3>
            <p className="text-sm text-muted-foreground">Per ricevere i dati post-chiamata da ElevenLabs</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-border">
            {config?.webhook_secret_configured ? (
              <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                {config?.webhook_secret_configured ? 'Webhook secret configurato' : 'Webhook secret non configurato'}
              </p>
              {config?.webhook_secret_configured && config?.webhook_secret_hint ? (
                <p className="text-xs text-muted-foreground font-mono mt-0.5">{config.webhook_secret_hint}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Configura il secret per ricevere i dati delle conversazioni
                </p>
              )}
            </div>
            {config?.webhook_secret_configured && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleTestWebhook}
                disabled={testingWebhook}
                className="flex-shrink-0 border-border"
              >
                {testingWebhook ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            )}
          </div>

          {webhookTestResult && (
            <div className={`flex items-center gap-2 p-3 rounded-lg border ${webhookTestResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              {webhookTestResult.success ? (
                <CheckCircle className="h-4 w-4 text-emerald-500" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
              <p className="text-sm">
                {webhookTestResult.success ? 'Webhook endpoint raggiungibile e funzionante' : (webhookTestResult.error || 'Test webhook fallito')}
              </p>
            </div>
          )}

          <div>
            <Label className="text-sm font-medium text-foreground mb-1.5 block">
              <Link className="h-3.5 w-3.5 inline mr-1" />
              Webhook URL (da configurare su ElevenLabs)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={`${window.location.origin}/api/elevenlabs/webhooks/post-call`}
                className="bg-muted border-border text-foreground font-mono text-xs"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-shrink-0 border-border"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/api/elevenlabs/webhooks/post-call`);
                  toast({ title: 'Copiato!', description: 'Webhook URL copiato negli appunti' });
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Copia questa URL e incollala su elevenlabs.io → Developers → Webhooks → Aggiungi Endpoint. Seleziona l'evento <b>post_call_transcription</b>.
            </p>
          </div>

          <div>
            <Label className="text-sm font-medium text-foreground mb-1.5 block">Signing Secret</Label>
            <Input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder={config?.webhook_secret_configured ? 'Inserisci nuovo secret per aggiornare' : 'Incolla il webhook signing secret'}
              className="bg-muted border-border text-foreground"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Dopo aver creato l'endpoint su ElevenLabs, copia il Signing Secret e incollalo qui
            </p>
          </div>

          <Button
            onClick={handleSaveWebhook}
            disabled={savingWebhook || !webhookSecret.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {savingWebhook ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva Webhook Secret
          </Button>
        </div>
      </div>

      {/* SEZIONE 3: Guida */}
      <div className="bg-card rounded-lg border border-border p-6">
        <Button
          variant="outline"
          onClick={() => setShowDocs(!showDocs)}
          className="w-full justify-between border-border text-foreground hover:bg-muted"
        >
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            <span>Guida alla Configurazione</span>
          </div>
          {showDocs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>

        {showDocs && (
          <div className="bg-muted rounded-lg p-4 mt-3 space-y-4">
            <div>
              <h5 className="font-semibold text-foreground mb-2">1. Come Ottenere l'API Key</h5>
              <ul className="text-sm text-foreground space-y-1 list-disc list-inside">
                <li>Vai su elevenlabs.io → Il tuo profilo → API Keys</li>
                <li>Copia la chiave API (inizia con "xi_")</li>
                <li>Incolla nel campo sopra e clicca "Testa API Key"</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-foreground mb-2">2. Configurazione Agenti su ElevenLabs</h5>
              <ul className="text-sm text-foreground space-y-1 list-disc list-inside">
                <li>Gli agenti vengono creati e gestiti dalla sezione "Agenti AI" di questa pagina</li>
                <li>EDG VoIP sincronizza automaticamente gli agenti con ElevenLabs</li>
                <li>Per ogni agente puoi scegliere: voce, LLM, knowledge base, tools</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-foreground mb-2">3. Configurazione SIP Trunk (per chiamate telefoniche) — MANUALE</h5>
              <div className="p-2 bg-amber-50 border border-amber-200 rounded mb-2">
                <p className="text-xs text-amber-800 font-medium flex items-center gap-1">
                  <span>&#9888;</span> La configurazione del SIP Trunk va fatta MANUALMENTE sulla Dashboard ElevenLabs
                </p>
              </div>
              <ul className="text-sm text-foreground space-y-1 list-disc list-inside">
                <li>Su ElevenLabs Dashboard → Phone Numbers → seleziona il numero → SIP Trunk</li>
                <li><strong>Inbound Trunk</strong>: aggiungi <code className="px-1 bg-muted rounded">{import.meta.env.VITE_SIP_PUBLIC_HOST || '85.94.215.109'}/32</code> come unico indirizzo autorizzato (gateway centrale, identico a tutti gli altri provider)</li>
                <li><strong>Outbound Trunk</strong>: indirizzo = <code className="px-1 bg-muted rounded">{import.meta.env.VITE_SIP_PUBLIC_HOST || '85.94.215.109'}</code> (o il dominio SBC), Transport = TLS porta 5061</li>
                <li><strong>Media Encryption</strong>: impostare su "allowed"</li>
                <li>Codec: G.711 (PCMU/PCMA) — configurato automaticamente lato gateway</li>
                <li>Il routing SIP/media verso sip.rtc.elevenlabs.io viene terminato dal gateway centrale (signaling + RTP escono dallo stesso IP)</li>
                <li className="text-amber-700 font-medium">Attenzione: ElevenLabs non offre API per il trunk SIP, quindi questa configurazione è esclusivamente manuale</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-foreground mb-2">4. Widget WebRTC (per sito web)</h5>
              <ul className="text-sm text-foreground space-y-1 list-disc list-inside">
                <li>Il widget si configura dalla scheda agente nella sezione "Canale"</li>
                <li>Genera automaticamente il codice embed da inserire nel sito</li>
                <li>Qualità audio HD (Opus 48kHz) - superiore al telefono</li>
                <li>Compatibile con qualsiasi sito web</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-foreground mb-2">5. Webhook Post-Call (per trascrizioni)</h5>
              <ul className="text-sm text-foreground space-y-1 list-disc list-inside">
                <li>Vai su elevenlabs.io → Developers → Webhooks</li>
                <li>Clicca "Add Endpoint" e incolla la Webhook URL mostrata sopra</li>
                <li>Seleziona l'evento <b>post_call_transcription</b></li>
                <li>Dopo aver creato l'endpoint, copia il <b>Signing Secret</b> e incollalo nella sezione sopra</li>
                <li>Senza questo webhook, le trascrizioni ElevenLabs non verranno salvate</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-foreground mb-2">6. Provider LLM</h5>
              <ul className="text-sm text-foreground space-y-1 list-disc list-inside">
                <li>OpenAI: Usa la chiave API già configurata nel sistema</li>
                <li>Claude, Gemini, Custom: Richiedono configurazione aggiuntiva nel modal agente</li>
                <li>ElevenLabs Native: Usa i modelli interni di ElevenLabs</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-foreground mb-2">7. Costi</h5>
              <ul className="text-sm text-foreground space-y-1 list-disc list-inside">
                <li>I costi dipendono dal piano ElevenLabs del tenant</li>
                <li>Confronto: ElevenLabs ~$0.08-0.10/min vs OpenAI Realtime ~$0.30/min</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* SEZIONE 4: Analytics */}
      <div className="bg-card rounded-lg border border-border p-6">
        <Button
          variant="outline"
          onClick={() => setShowAnalytics(!showAnalytics)}
          className="w-full justify-between border-border text-foreground hover:bg-muted"
        >
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span>Dashboard Conversazioni</span>
          </div>
          {showAnalytics ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>

        {showAnalytics && (
          <div className="mt-4 space-y-4">
            {analyticsLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
              </div>
            ) : analytics ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-muted rounded-lg p-4 border border-border text-center">
                    <MessageSquare className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-foreground">{analytics.stats?.total_conversations || 0}</p>
                    <p className="text-xs text-muted-foreground">Conversazioni Totali</p>
                  </div>
                  <div className="bg-muted rounded-lg p-4 border border-border text-center">
                    <Clock className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-foreground">
                      {Math.round(Number(analytics.stats?.avg_duration || 0))}s
                    </p>
                    <p className="text-xs text-muted-foreground">Durata Media</p>
                  </div>
                  <div className="bg-muted rounded-lg p-4 border border-border text-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-foreground">{analytics.stats?.completed || 0}</p>
                    <p className="text-xs text-muted-foreground">Completate</p>
                  </div>
                  <div className="bg-muted rounded-lg p-4 border border-border text-center">
                    <Phone className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-foreground">
                      {Math.round(Number(analytics.stats?.total_duration || 0) / 60)}m
                    </p>
                    <p className="text-xs text-muted-foreground">Tempo Totale</p>
                  </div>
                </div>

                {analytics.recent && analytics.recent.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-foreground">Conversazioni Recenti</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={fetchAnalytics}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Aggiorna
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {analytics.recent.map((conv: ElevenLabsAnalyticsConversation, i: number) => (
                        <div key={conv.id || i} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${conv.status === 'completed' ? 'bg-green-500' : conv.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                            <div>
                              <p className="text-sm font-medium text-foreground">{conv.agent_name || 'Agente'}</p>
                              <p className="text-xs text-muted-foreground">
                                {conv.created_at ? new Date(conv.created_at).toLocaleString('it-IT') : '-'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {conv.duration_seconds && (
                              <Badge variant="outline" className="text-xs">
                                {Math.round(Number(conv.duration_seconds))}s
                              </Badge>
                            )}
                            <Badge className={
                              conv.status === 'completed' ? 'bg-green-100 text-green-700' :
                              conv.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {conv.status === 'completed' ? 'Completata' :
                               conv.status === 'failed' ? 'Fallita' : conv.status || 'N/A'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!analytics.recent || analytics.recent.length === 0) && (
                  <div className="text-center py-6 text-muted-foreground">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nessuna conversazione registrata</p>
                    <p className="text-xs">Le conversazioni appariranno qui dopo le prime chiamate</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <p className="text-sm">Impossibile caricare i dati analytics</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
